import { FC, useState, useEffect, useCallback } from 'react';
import { Check, Wallet, Import, Loader2, Trash2, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTurnkeyWallet, SolanaWalletInfo } from '@/providers/TurnkeyProvider';
import { Connection, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { getSolanaEndpoint } from '@/lib/solana/config';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface WalletSwitcherProps {
  onSelect?: () => void;
  className?: string;
}

// Cache balances to avoid excessive RPC calls (per-address)
const balanceCache = new Map<string, { balance: number; timestamp: number }>();
const CACHE_TTL = 15000; // 15 seconds - shorter for more responsive updates

// LocalStorage key for hidden wallets
const HIDDEN_WALLETS_KEY = 'dextoro_hidden_wallets';

// Get hidden wallets from localStorage
const getHiddenWallets = (): Set<string> => {
  try {
    const stored = localStorage.getItem(HIDDEN_WALLETS_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
};

// Save hidden wallets to localStorage
const saveHiddenWallets = (wallets: Set<string>) => {
  localStorage.setItem(HIDDEN_WALLETS_KEY, JSON.stringify([...wallets]));
};

export const WalletSwitcher: FC<WalletSwitcherProps> = ({ onSelect, className }) => {
  const { allSolanaWallets, activeWalletIndex, setActiveWallet } = useTurnkeyWallet();
  const [balances, setBalances] = useState<Map<string, number | null>>(new Map());
  const [loadingBalances, setLoadingBalances] = useState<Set<string>>(new Set());
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [hiddenWallets, setHiddenWallets] = useState<Set<string>>(getHiddenWallets);
  const [isEditMode, setIsEditMode] = useState(false);
  const [walletToRemove, setWalletToRemove] = useState<SolanaWalletInfo | null>(null);

  // Auto-unhide wallets when they're re-imported
  // This handles the case where a user disconnects then reimports the same wallet
  useEffect(() => {
    if (hiddenWallets.size === 0) return;

    const currentAddresses = new Set(allSolanaWallets.map(w => w.address));
    const addressesToUnhide = [...hiddenWallets].filter(addr => currentAddresses.has(addr));

    // If there are newly imported wallets that were hidden, unhide them
    if (addressesToUnhide.length > 0) {
      // Check if these are actually new imports (not just existing hidden wallets)
      // We do this by checking if the wallet count increased
      const prevCount = localStorage.getItem('dextoro_wallet_count');
      const currentCount = allSolanaWallets.length;

      if (prevCount && currentCount > parseInt(prevCount, 10)) {
        // New wallet was added, unhide any matching addresses
        const newHidden = new Set(hiddenWallets);
        addressesToUnhide.forEach(addr => newHidden.delete(addr));
        setHiddenWallets(newHidden);
        saveHiddenWallets(newHidden);
      }

      localStorage.setItem('dextoro_wallet_count', String(currentCount));
    }
  }, [allSolanaWallets, hiddenWallets]);

  // Filter out hidden wallets
  const visibleWallets = allSolanaWallets.filter(w => !hiddenWallets.has(w.address));

  // Fetch balances for all wallets
  const fetchBalances = useCallback(async () => {
    if (allSolanaWallets.length === 0) return;

    const connection = new Connection(getSolanaEndpoint(), 'confirmed');

    for (const wallet of allSolanaWallets) {
      // Check cache first
      const cached = balanceCache.get(wallet.address);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        setBalances(prev => new Map(prev).set(wallet.address, cached.balance));
        continue;
      }

      setLoadingBalances(prev => new Set(prev).add(wallet.address));

      try {
        const balance = await connection.getBalance(new PublicKey(wallet.address));
        const solBalance = balance / LAMPORTS_PER_SOL;

        // Update cache
        balanceCache.set(wallet.address, { balance: solBalance, timestamp: Date.now() });

        setBalances(prev => new Map(prev).set(wallet.address, solBalance));
      } catch (error) {
        console.error('[WalletSwitcher] Error fetching balance:', wallet.address.slice(0, 8), error);
        setBalances(prev => new Map(prev).set(wallet.address, null));
      } finally {
        setLoadingBalances(prev => {
          const next = new Set(prev);
          next.delete(wallet.address);
          return next;
        });
      }
    }
  }, [allSolanaWallets]);

  // Fetch on mount and when wallets change
  useEffect(() => {
    fetchBalances();
  }, [fetchBalances, fetchTrigger]);

  // Re-fetch balances every time component mounts (dropdown opens)
  useEffect(() => {
    setFetchTrigger(prev => prev + 1);
  }, []);

  // Find the original index in allSolanaWallets for a visible wallet
  const getOriginalIndex = (wallet: SolanaWalletInfo): number => {
    return allSolanaWallets.findIndex(w => w.address === wallet.address);
  };

  // Get active wallet from visible wallets
  const activeVisibleIndex = visibleWallets.findIndex(
    w => getOriginalIndex(w) === activeWalletIndex
  );

  // Handle confirming removal of an imported wallet
  const confirmRemoveWallet = () => {
    if (!walletToRemove) return;

    // Cannot remove the last visible wallet
    if (visibleWallets.length <= 1) {
      toast.error('Cannot remove the only wallet');
      setWalletToRemove(null);
      return;
    }

    // Hide the wallet
    const newHidden = new Set(hiddenWallets);
    newHidden.add(walletToRemove.address);
    setHiddenWallets(newHidden);
    saveHiddenWallets(newHidden);

    // If removing the active wallet, switch to the embedded wallet (first non-imported)
    const originalIndex = getOriginalIndex(walletToRemove);
    if (originalIndex === activeWalletIndex) {
      const embeddedWallet = visibleWallets.find(w => !w.isImported && w.address !== walletToRemove.address);
      const fallbackWallet = visibleWallets.find(w => w.address !== walletToRemove.address);
      const newActiveWallet = embeddedWallet || fallbackWallet;
      if (newActiveWallet) {
        const newActiveIndex = getOriginalIndex(newActiveWallet);
        setActiveWallet(newActiveIndex);
      }
    }

    toast.success('Wallet removed', {
      description: `${walletToRemove.walletName} has been removed`,
    });
    setWalletToRemove(null);
    setIsEditMode(false);
  };

  // Handle initiating wallet removal (shows confirmation)
  const handleRemoveWallet = (wallet: SolanaWalletInfo, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent selecting the wallet
    setWalletToRemove(wallet);
  };

  // Check if any imported wallets exist (to show/hide edit button)
  const hasImportedWallets = visibleWallets.some(w => w.isImported);

  if (visibleWallets.length <= 1) {
    return null; // Don't show switcher if only 1 visible wallet
  }

  const handleSelect = (wallet: SolanaWalletInfo) => {
    // Don't allow selection in edit mode
    if (isEditMode) return;
    const originalIndex = getOriginalIndex(wallet);
    setActiveWallet(originalIndex);
    onSelect?.();
  };

  const formatBalance = (balance: number | null | undefined) => {
    if (balance === null || balance === undefined) return '–';
    return balance.toFixed(4);
  };

  const shortAddress = (address: string) =>
    `${address.slice(0, 4)}...${address.slice(-4)}`;

  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex items-center justify-between px-2 py-1.5">
        <p className="text-xs font-medium text-muted-foreground">
          Switch Wallet
        </p>
        {hasImportedWallets && (
          <button
            onClick={() => setIsEditMode(!isEditMode)}
            className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
          >
            {isEditMode ? 'Done' : 'Edit'}
          </button>
        )}
      </div>
      {visibleWallets.map((wallet) => {
        const originalIndex = getOriginalIndex(wallet);
        const isActive = originalIndex === activeWalletIndex;
        const canRemove = wallet.isImported && visibleWallets.length > 1;

        return (
          <div
            key={wallet.address}
            className="relative"
          >
            <button
              onClick={() => handleSelect(wallet)}
              disabled={isEditMode}
              className={cn(
                'w-full flex items-center gap-3 px-2 py-2 rounded-md text-sm transition-colors',
                !isEditMode && 'hover:bg-white/5',
                isActive && 'bg-white/10',
                isEditMode && 'cursor-default'
              )}
            >
              <div className="flex-shrink-0">
                {isActive ? (
                  <Check className="w-4 h-4 text-primary" />
                ) : wallet.isImported ? (
                  <Import className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <Wallet className="w-4 h-4 text-primary" />
                )}
              </div>

              <div className="flex-1 text-left min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">
                    {wallet.walletName}
                  </span>
                  {wallet.isImported && (
                    <span className="px-1.5 py-0.5 text-[10px] font-medium bg-primary/10 text-primary rounded">
                      Imported
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground font-mono">
                  {shortAddress(wallet.address)}
                </div>
              </div>

              {/* Balance or edit mode actions */}
              <div className="flex-shrink-0 flex items-center gap-2">
                {isEditMode ? (
                  // Edit mode: show remove button for imported, primary badge for embedded
                  wallet.isImported ? (
                    canRemove && (
                      <button
                        onClick={(e) => handleRemoveWallet(wallet, e)}
                        className={cn(
                          'p-1.5 rounded-md',
                          'bg-destructive/10 hover:bg-destructive/20',
                          'text-destructive hover:text-destructive',
                          'transition-colors'
                        )}
                        title="Remove wallet"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )
                  ) : (
                    <span className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground rounded">
                      <Lock className="w-2.5 h-2.5" />
                      Primary
                    </span>
                  )
                ) : (
                  // Normal mode: show balance
                  loadingBalances.has(wallet.address) ? (
                    <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                  ) : (
                    <span className="text-xs font-medium">
                      {formatBalance(balances.get(wallet.address))} SOL
                    </span>
                  )
                )}
              </div>
            </button>
          </div>
        );
      })}

      {/* Confirmation dialog for removing wallet */}
      <AlertDialog open={!!walletToRemove} onOpenChange={(open) => !open && setWalletToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove wallet?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {walletToRemove?.walletName} ({walletToRemove && shortAddress(walletToRemove.address)}) from your account?
              You can re-import it later using your private key.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemoveWallet}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WalletSwitcher;
